/**
 * BrandCollabBlock — renders a brand's promotable campaign asset (product,
 * coupon, lead form, or full landing page) inline on a creator's Action
 * Page. Extracted to its own file to avoid Babel stack-overflow when the
 * landing_page branch recurses into ActionBlockRenderer.
 */
import { Handshake } from "lucide-react";
import ActionBlockRenderer, { ProductBlock, CouponBlock, LeadFormBlock } from "./ActionBlockRenderer";

export default function BrandCollabBlock({ block, editable }) {
  const c = block.config || {};
  const status = c._status;
  const camp = c._campaign;
  const asset = c._asset;

  if (editable) {
    if (!c.campaign_id) {
      return (
        <div className="ah-block text-center" data-testid={`block-brand-collab-empty-${block.block_id}`}>
          <Handshake className="w-5 h-5 mx-auto ah-muted" />
          <p className="text-xs ah-muted mt-1">No campaign linked. Open the editor to paste a share code.</p>
        </div>
      );
    }
    return (
      <div className="ah-block text-center" data-testid={`block-brand-collab-preview-${block.block_id}`}>
        <div className="flex items-center justify-center gap-1.5 mb-1">
          <Handshake className="w-4 h-4 ah-accent" strokeWidth={2.5} />
          <p className="font-bold text-sm">Brand collab</p>
        </div>
        <p className="text-xs ah-muted">Campaign: {c.campaign_id}</p>
        <p className="text-[10px] ah-muted">Live preview shows on the public page.</p>
      </div>
    );
  }

  if (status === "expired" || status === "capped" || status === "unavailable" || !camp) {
    return (
      <div className="ah-block text-center" data-testid={`block-brand-collab-inactive-${block.block_id}`}>
        <p className="text-xs ah-muted">This campaign is no longer available.</p>
      </div>
    );
  }

  const kind = asset?.kind;
  return (
    <div className="ah-block space-y-2" data-testid={`block-brand-collab-${block.block_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex items-center gap-1.5">
          <Handshake className="w-3.5 h-3.5 flex-shrink-0 ah-accent" strokeWidth={2.5} />
          <p className="text-[10px] uppercase tracking-wider font-bold ah-muted">Sponsored by {camp.brand_name}</p>
        </div>
        {/* v146.68 — Commission / flat-price info is a private
            payout detail between the brand and the influencer. Hide
            it on the public page (customers don't need or want to
            see it); keep it visible only in the creator's editor
            preview so they can double-check their own deal terms. */}
        {editable && (() => {
          const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£" }[(camp.currency || "INR").toUpperCase()] || "";
          if (Number(camp.flat_price) > 0 && camp.flat_price_metric) {
            return (
              <span className="border border-current text-[9px] font-bold px-1 py-0.5 ah-accent"
                    data-testid={`block-brand-collab-flat-${block.block_id}`}>
                {sym}{Number(camp.flat_price)} / {camp.flat_price_metric}
              </span>
            );
          }
          if (camp.commission_pct > 0) {
            return (
              <span className="border border-current text-[9px] font-bold px-1 py-0.5 ah-accent"
                    data-testid={`block-brand-collab-commission-${block.block_id}`}>
                {camp.commission_pct}% commission
              </span>
            );
          }
          return null;
        })()}
      </div>
      {kind === "product" && asset.product && (
        <ProductBlock
          block={{ block_id: `${block.block_id}-p`, config: asset.product }}
          editable={editable}
        />
      )}
      {kind === "coupon" && asset.coupon && (
        <CouponBlock
          block={{ block_id: `${block.block_id}-c`, config: asset.coupon }}
          editable={editable}
        />
      )}
      {kind === "lead_form" && asset.config && (
        <LeadFormBlock
          block={{ block_id: block.block_id, config: asset.config }}
          editable={editable}
        />
      )}
      {kind === "landing_page" && asset.page && Array.isArray(asset.blocks) && (
        <div className="space-y-2" data-testid={`block-brand-collab-landing-${block.block_id}`}>
          {asset.page.title && (
            <p className="font-bold text-sm text-center pb-1 border-b border-current/20">{asset.page.title}</p>
          )}
          {asset.blocks
            .filter((nested) => nested.type !== "brand_collab")
            .slice(0, 20)
            .map((nested) => (
              <ActionBlockRenderer
                key={nested.block_id}
                block={{
                  ...nested,
                  block_id: `${block.block_id}::${nested.block_id}`,
                }}
                editable={editable}
              />
            ))}
          {asset.blocks.length > 20 && (
            <p className="text-[10px] ah-muted text-center">
              +{asset.blocks.length - 20} more blocks on the brand&apos;s page.
            </p>
          )}
        </div>
      )}
      {asset?.missing && <p className="text-xs ah-muted text-center">Campaign asset temporarily unavailable.</p>}
    </div>
  );
}
